import { memo, useEffect, useMemo, useRef, useState } from "react";
import { useCheckpoints, useOrgs, useRoles, useUserDetail, useAllPermissionSet, useDynamicModules, usePlant, useUserPermissions } from "../../services/hooks";
import { valid_email, valid_mobile, valid_password } from "../../services/validation";
import { useForm, Controller } from "react-hook-form";

import { Button, Checkbox, Dropdown, Input, Loader } from "../utils";
import { toastMessage } from "../utils";
import { capitalizeCamelCase } from "../../services/functions";
import { useAddUserMutation, useUpdateUserMutation } from "../../api/userSlice";
import { useGetApiListDetailOnCallMutation } from "../../api/vmsSlice";
import "./AddUser.css";
import { AccordionWrapperVanilla, CHECKPOINT_CHILD_PERMS, getGeneralPermissions, getWarehousePermissions } from "./permissionHelpers";
import { useLocation, useNavigate } from "react-router-dom";
import { buttonClasses } from "../utils/theme";

const defaultForm = {
	name: "",
	email: "",
	mobile: "",
	password: "",
	confirmPassword: "",
	permissions: {},
	userRole: "",
	userRoleId: "",
	userRoleHierarchy: 0,
	orgId: "",
	orgName: "",
};

function fillUpdateData(data, plants = []) {
	if (!data) {
		// For new user, initialize with empty permissions
		return {
			...defaultForm,
			permissions: {
				vms: plants.reduce((acc, plant) => {
					acc[`plant-${plant._id}`] = undefined;
					return acc;
				}, {}),
			},
		};
	}
	const permissions = {};

	if (data?.userPermissions.length > 0) {
		data.userPermissions.forEach((ele) => {
			if (ele.module) {
				permissions[ele.module] = {};
				for (let perm in ele.permissions) {
					permissions[ele.module][perm] = ele.permissions[perm];
				}
			}
		});
	}
	return {
		name: data?.name,
		email: data?.email,
		mobile: data?.mobile,
		orgId: data?.orgId,
		orgName: data?.orgName,
		permissions,
		userRole: data.userRole,
		userRoleId: data.userRoleId,
		userRoleHierarchy: data.userRoleHierarchy,
	};
}

const permObj = { dependancy: [], strict: false, value: true };

function getApiPermissionId(apiItem = {}) {
	return String(apiItem._id || "");
}

function makeCheckpointPerms(checkpoints = []) {
	return checkpoints.reduce((res, curr) => {
		const key = `vms.checkpoint-${curr._id}`;
		const listConfigPerms = curr.listingConfig?.reduce((res, curr) => {
			res[`${key}-${curr.type}`] = permObj;
			return res;
		}, {});

		let additionalPerms = {};
		if (curr.assetConfig && Object.keys(curr.assetConfig).length) {
			for (let permType in curr.assetConfig) {
				if (curr.assetConfig[permType]) {
					additionalPerms[`${key}-${permType}`] = {
						...permObj,
						dependancy: [permType],
						strict: true,
					};
				}
			}
		}

		res[curr._id] = {
			[key]: permObj,
			...Object.fromEntries(CHECKPOINT_CHILD_PERMS.map((perm) => [`${key}-${perm}`, permObj])),
			...(listConfigPerms || {}),
			...additionalPerms,
		};
		return res;
	}, {});
}

function AddUserForm() {
	const navigate = useNavigate();
	const location = useLocation();
	const { updateData } = location.state || {};
	const { filterData } = location.state || {};
	const isUpdate = location.pathname.includes("edit");
	const { plantOptions, clearPlants, getPlantDetails, isLoading: isPlantLoading, plants } = usePlant();
	const {
		handleSubmit,
		control,
		watch,
		setValue,
		getValues,
		formState: { errors },
	} = useForm({
		defaultValues: updateData ? fillUpdateData(updateData, plants) : defaultForm,
	});

	const orgId = watch("orgId");
	const password = watch("password");

	const [handleAdd, { isLoading: isAdding }] = useAddUserMutation();
	const [handleUpdate, { isLoading: isUpdating }] = useUpdateUserMutation();

	const [passwordType, setPasswordType] = useState("password");
	const [confirmPasswordType, setConfirmMPasswordType] = useState("password");

	const { userDetail, isSuperAdmin } = useUserDetail();
	const { userPermissions } = useUserPermissions();

	const checkpointFilterRef = useRef({});
	const { checkpoints, getCheckpoints, isLoading: isChkptLoading, clearCheckpointData } = useCheckpoints(null, "adduser");

	const { orgOptions } = useOrgs({}, { skip: !isSuperAdmin });
	const orgValue = orgOptions.find((_) => _.value == orgId) || null;

	const allowAddPlant = orgValue?.data?.permissions?.managePlant?.value || userPermissions?.managePlant?.value;

	const roleFitlerRef = useRef({});
	const { roleOptions, getRoles, isLoading: roleLoading } = useRoles();

	const { permissions: permissionSetPerms, isLoading: isPermLoading } = useAllPermissionSet();

	const { dynamicModules, getDynamicModules } = useDynamicModules(null, "dynamicModuleAddUser");
	const [getApiList, { data: apiListData, isLoading: isApiListLoading }] = useGetApiListDetailOnCallMutation({ fixedCacheKey: "addUserApiList" });
	const apiList = apiListData?.data || [];

	useEffect(() => {
		if (isUpdate) {
			if (updateData) {
				let vmsPerms = updateData.userPermissions?.find((_) => _.module == "vms")?.permissions ?? {};
				let plantIdSet = new Set();
				for (let permKey in vmsPerms) {
					if (permKey.includes("plant")) {
						plantIdSet.add(permKey.split("-")[1]);
					}
				}
				setSelectedPlants([...plantIdSet]);
			} else {
				navigate("/users");
			}
		} else {
			if (updateData) {
				let vmsPerms = updateData.userPermissions?.find((_) => _.module == "vms")?.permissions ?? {};
				let plantIdSet = new Set();
				for (let permKey in vmsPerms) {
					if (permKey.includes("plant")) {
						plantIdSet.add(permKey.split("-")[1]);
					}
				}
				setSelectedPlants([...plantIdSet]);
			}
		}
	}, []);

	useEffect(() => {
		if (!userDetail) return;
		if (userDetail.userType == "superAdmin") return;
		if (userDetail.userType != "superAdmin" && roleOptions.length) return;
		roleFitlerRef.current = { orgId: userDetail.orgId };
		getRoles(roleFitlerRef.current);
	}, [userDetail]);

	useEffect(() => {
		const effectiveOrgId = orgId || userDetail?.orgId;

		if (!effectiveOrgId && userDetail?.userType == "superAdmin") {
			clearCheckpointData();
			clearPlants();
			return;
		}
		if (!effectiveOrgId) return;

		getPlantDetails({ orgId: effectiveOrgId });

		roleFitlerRef.current = { orgId: effectiveOrgId };
		getRoles(roleFitlerRef.current);

		checkpointFilterRef.current = { orgId: effectiveOrgId };
		getCheckpoints(checkpointFilterRef.current);

		getDynamicModules({ orgId: effectiveOrgId });
		getApiList({ orgId: effectiveOrgId, filters : { forLogs: true} });
	}, [orgId, userDetail]);

	useEffect(() => {
		if (!userDetail) return;
		if (userDetail.userType == "superAdmin") return;
		if (userDetail.userType != "superAdmin" && dynamicModules.length) return;
		getDynamicModules({ orgId: userDetail.orgId });
	}, [userDetail]);

	const changeType = () => {
		setPasswordType((prev) => (prev === "password" ? "text" : "password"));
	};

	const changeConfirmType = () => {
		setConfirmMPasswordType((prev) => (prev === "password" ? "text" : "password"));
	};

	const handleRoleCheckpointPerms = (roleId, checkpointIds = []) => {
		const otherRoles = roleOptions.filter((_) => _.value != roleId);
		otherRoles.forEach((otherRole) => {
			if (otherRole.data?.checkpoints) {
				const otherRoleChkptPerms = makeCheckpointPerms(checkpoints.filter((_) => otherRole.data?.checkpoints?.includes(_._id)));
				for (let chkptId in otherRoleChkptPerms) {
					for (let permKey in otherRoleChkptPerms[chkptId]) {
						setValue(`permissions.${permKey}`, false, { shouldDirty: true });
					}
				}
			}
		});

		const currentRoleChkptPerms = makeCheckpointPerms(checkpoints.filter((_) => checkpointIds.includes(_._id)));
		for (let chkptId in currentRoleChkptPerms) {
			for (let permKey in currentRoleChkptPerms[chkptId]) {
				setValue(`permissions.${permKey}`, true, { shouldDirty: true });
			}
		}
	};

	const onDropdownChange = (option, name) => {
		const value = option?.value || "";
		setValue(name, value);

		if (name === "userRole") {
			setValue("userRoleId", option?.data?._id || "");
			setValue("userRoleHierarchy", Number(option?.data?.hierarchy) || 0);
			if (option?.data?.checkpoints) {
				handleRoleCheckpointPerms(value, option.data.checkpoints);
			}
		}
	};

	const updateWMSUser = async (userId, userPermission) => {
		// console.log('updateUserDetail', userPermission);
		const makePermissionForWMS = (permission) => {
			const checkpointActions = ["", "arrived", "manualEntry", "viewDetail", "viewListing", "viewSubmit"];
			
			// Precompute plant to checkpoints mapping
			const plantCheckpointsMap = new Map();
			checkpoints.forEach(cp => {
				if (!plantCheckpointsMap.has(cp.plantId)) {
				plantCheckpointsMap.set(cp.plantId, []);
				}
				plantCheckpointsMap.get(cp.plantId).push({
				id: cp._id,
				displayName: cp.displayName
				});
			});

			return plantOptions
				.map((plant) => {
				const plantPermission = `plant-${plant.value}`;
				const plantPermissionObj = permission.vms[plantPermission];

				// Early return if plant doesn't have permission
				if (!plantPermissionObj?.value) {
					return null;
				}

				const plantCheckpoints = plantCheckpointsMap.get(plant.value) || [];
				const permissions = [];
				const otherPermission = {};

				// Process checkpoints permissions
				plantCheckpoints.forEach((checkpoint) => {
					checkpointActions.forEach((action) => {
					const permissionKey = action 
						? `checkpoint-${checkpoint.id}-${action}` 
						: `checkpoint-${checkpoint.id}`;
					
					const permissionObj = permission.vms[permissionKey];
					const hasPermission = permissionObj?.value || 
						(!action && permission.vms[`checkpoint-${checkpoint.id}`]);

					if (hasPermission) {
						permissions.push({
						permissionId: permissionKey,
						permissionName: action 
							? `${capitalizeWords(action)} ${checkpoint.displayName}` 
							: checkpoint.displayName,
						});
					}
					});
				});

				// Process other permissions
				Object.entries(permission).forEach(([key, value]) => {
					const lowerKey = key.toLowerCase();
					
					if (lowerKey !== 'vms' && lowerKey !== 'test' && Object.keys(value).length > 0) {
						if (lowerKey === "api") {
							Object.entries(value).forEach(([propKey, propValue]) => {
								const stringKey = String(propKey);
								if (stringKey.includes("plant")) {
									const keyArray = stringKey.split("-");
									if (keyArray.length > 2 && keyArray[1] === plant.value) {
										const apiId = keyArray.slice(2).join("-");
										const apiName = apiList.find((_) => _._id == apiId)?.name || apiId;
										permissions.push({
											permissionId: propKey,
											permissionName: apiName,
										});
									}
								} else {
									otherPermission[propKey] = propValue;
								}
							});
						} else {
							Object.assign(otherPermission, value);
						}
					} else if (lowerKey === 'vms') {
					Object.entries(value).forEach(([propKey, propValue]) => {
						const stringKey = String(propKey);
						
						if (!stringKey.includes('plant') && !stringKey.includes('checkpoint-')) {
						otherPermission[propKey] = propValue;
						}
						
						if (stringKey.includes('plant')) {
						const keyArray = stringKey.split('-');
						if (keyArray.length > 2 && keyArray[1] === plant.value) {
							permissions.push({
							permissionId: propKey,
							permissionName: keyArray[2],
							});
						}
						}
					});
					}
				});

				return {
					warehouseID: plant.data?.sourceReferenceId,
					permissions,
					otherPermissions: otherPermission,
				};
				})
				.filter(Boolean);
			};
		

		const updatedPermissionSet = makePermissionForWMS(userPermission);
		const payload = {
			userId: userId,
			permissionSet: updatedPermissionSet,
		};
		const apiURL = `${import.meta.env.VITE_WMS_URL}users/updateWarehouseAndPermissions`;
		const secretKey = localStorage.getItem("secretkey");
		try {
			const response = await fetch(apiURL, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: secretKey || "",
					project: localStorage.getItem("project") || "LYNKID",
				},
				body: JSON.stringify(payload),
			});

			if (!response.ok) throw new Error("API call failed");
			await response.json();
		} catch (error) {
			console.error("Error: in wms api", error);
		}
	};

	const checkUserProject = async (userEmail, updateUserDetail) => {
		const payload = {
			projectId: "LYNKID",
			email: userEmail.toLowerCase(),
			selection: ["projects"],
		};
		const apiURL = `${import.meta.env.VITE_LYNKTRAC_SSO_URL}user/userDetailsForAnyUser`;
		const secretKey = localStorage.getItem("secretkey");
		try {
			const response = await fetch(apiURL, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: secretKey || "",
				},
				body: JSON.stringify(payload),
			});

			if (!response.ok) throw new Error("API call failed");
			const data = await response.json();
			if (data.data) {
				let projects = data.data.projects;
				if (projects.includes("WMS")) {
					await updateWMSUser(data.data._id, updateUserDetail);
				}
			}
		} catch (error) {
			console.error("Error: in sso api for project", error);
		}
	};

	const onSubmit = async (data) => {
		// Filter out undefined permissions
		const filteredPermissions = {};
		let plantPermAbsent = true;
		for (const permModule in data.permissions) {
			filteredPermissions[permModule] = {};
			for (let perm in data.permissions[permModule]) {
				if (data.permissions[permModule][perm]) {
					if(perm.includes("plant")) plantPermAbsent = false;
					filteredPermissions[permModule][perm] = { ...permObj };
				}
			}
		}
		if(plantPermAbsent){
			toastMessage(false, "Please select at least one warehouse")
			return;
		}
		const payload = {
			...data,
			permissions: filteredPermissions, //filtered permissions without undefined data
			orgName: orgValue?.label || userDetail.orgName,
			orgId: orgValue?.value || userDetail.orgId,
			userType: userDetail.userType == "superAdmin" ? "admin" : "user", //userDetail.userType == "admin" ? "user" : undefined,
			project: userDetail.userType == "superAdmin" ? "lynkid" : undefined,
			module: userDetail.userType == "admin" ? userDetail?.module || "Default" : "Default",
			userId: updateData?._id || undefined,
		};
		// console.log("Payload", payload);
		const {
			data: { error, message },
		} = updateData && isUpdate ? await handleUpdate(payload) : await handleAdd(payload);

		await checkUserProject(data.email, filteredPermissions);

		if (error) {
			toastMessage(false, message);
		} else {
			toastMessage(true, message);
			navigate("/users", { state: { filterData: filterData } });
		}
	};

	const [selectedPlants, setSelectedPlants] = useState([]);

	const plantCheckboxes = useMemo(() => {
		return plantOptions.map((plant) => {
			const plantKey = `vms.plant-${plant.value}`;
			const plantCheckpoints = checkpoints.filter((cp) => cp.plantId === plant.value);
			const checkpointPerms = makeCheckpointPerms(plantCheckpoints);

			const onChange = (isChecked) => {
				setSelectedPlants((prev) => (isChecked ? [...prev, plant.value] : prev.filter((pId) => pId != plant.value)));

				// when unselecting a plant, remove all permissions of its checkpoints and modules
				for (let id in checkpointPerms) {
					for (let childKey in checkpointPerms[id]) {
						setValue(`permissions.${childKey}`, isChecked, { shouldDirty: true });
					}
				}
				if (!isChecked) {
					const currentPerms = getValues("permissions");
					for (let permModule in currentPerms) {
						for (let perm in currentPerms[permModule]) {
							if (perm.startsWith(`plant-${plant.value}-`)) {
								setValue(`permissions.${permModule}.${perm}`, isChecked, { shouldDirty: true });
							}
						}
					}
				}
			};
			return <CheckboxController key={plantKey} name={plantKey} control={control} label={plant.label} setValue={setValue} onChange={onChange} />;
		});
	}, [plantOptions, checkpoints]);

	// Non-Plant Permissions (Default, TMS, etc. + dynamic modules), without plant prefix
	const nonPlantPermissions = getGeneralPermissions(permissionSetPerms, dynamicModules, { allowManagePlant: allowAddPlant }).map(({ module, perm }) => {
		const fullKey = `${module}.${perm}`;
		return <CheckboxController key={fullKey} name={fullKey} control={control} label={capitalizeCamelCase(perm)} setValue={setValue} />;
	});

	const areAllVmsPermsChecked = (plantId) => {
		const plantVmsPerms = Object.keys(permissionSetPerms.vms || {});
		return plantVmsPerms.every((perm) => {
			const fullKey = `vms.plant-${plantId}-${perm}`;
			return watch(`permissions.${fullKey}`);
		});
	};
	const handleSelectAllVmsPerms = (e, plantId) => {
		const { checked } = e.target;
		const vmsPerms = Object.keys(permissionSetPerms.vms || {});
		vmsPerms.forEach((perm) => {
			const fullKey = `vms.plant-${plantId}-${perm}`;
			setValue(`permissions.${fullKey}`, checked, { shouldDirty: true });
		});
	};

	// Plant accordions with VMS permissions only
	const plantAccordions = plantOptions
		.filter((_) => selectedPlants.includes(_.value))
		.map((plantOpt) => {
			const plantId = plantOpt.value;
			const plantCheckpoints = checkpoints.filter((cp) => cp.plantId === plantId);

			const checkpointPerms = makeCheckpointPerms(plantCheckpoints);
			const checkpointPermissions = [];

			for (let id in checkpointPerms) {
				const checkpoint = plantCheckpoints.find((cp) => cp._id === id);

				let childCheckboxes = [];
				for (let childKey in checkpointPerms[id]) {
					if (childKey != `vms.checkpoint-${id}`) {
						childCheckboxes.push(<CheckboxController key={childKey} name={childKey} control={control} label={capitalizeCamelCase(childKey.split("-").pop())} setValue={setValue} />);
					}
				}

				checkpointPermissions.push(
					<div key={id} className="checkpoint-perm-wrap">
						<CheckboxController name={`vms.checkpoint-${id}`} control={control} label={checkpoint.displayName} setValue={setValue} childrenKeys={Object.keys(checkpointPerms[id])} />
						<div className="checkpoint-perm-wrap-inner" style={{ display: "inline-flex" }}>
							{childCheckboxes}
						</div>
					</div>
				);
			}

			const apiPermissions = [];

			// Only add VMS permissions inside plant accordions
			const vmsPermissions = getWarehousePermissions(permissionSetPerms).map((perm) => {
				const prefixedKey = `vms.plant-${plantId}-${perm}`;
				return <CheckboxController key={prefixedKey} name={prefixedKey} control={control} label={capitalizeCamelCase(perm)} setValue={setValue} />;
			});

			apiList.forEach((apiItem) => {
				const apiPermId = getApiPermissionId(apiItem);
				if (apiPermId) {
					const prefixedKey = `api.plant-${plantId}-${apiPermId}`;
					apiPermissions.push(<CheckboxController key={prefixedKey} name={prefixedKey} control={control} label={apiItem.name} setValue={setValue} />);
				}
			});
			return (
				<AccordionWrapperVanilla key={plantId} title={plantOpt?.label}>
					<div className="plant-permissions-section">
						{checkpointPermissions.length > 0 && (
							<div className="checkpoint-perm-wrap-outer">
								<div style={{ fontWeight: "600", color:"var(--text-color)" }}>Checkpoint Permissions</div>
								{checkpointPermissions}
							</div>
						)}

						{vmsPermissions.length > 0 && (
							<>
								<div style={{ display: "flex", justifyContent: "space-between", marginTop: "1.5rem" }}>
									<div style={{ fontWeight: "600", color:"var(--text-color)" }}>Warehouse Permissions</div>
									<Checkbox label="Select All" checked={areAllVmsPermsChecked(plantId)} onChange={(e) => handleSelectAllVmsPerms(e, plantId)} />
								</div>
								<div className="perm_wrap grid grid-cols-2 gap-4">{vmsPermissions}</div>
							</>
						)}

						{isApiListLoading ? (
							<Loader size="1rem" />
						) : (
							apiPermissions.length > 0 && (
								<>
									<div style={{ fontWeight: "600", color:"var(--text-color)", marginTop: "1.5rem" }}>API Permissions</div>
									<div className="perm_wrap grid grid-cols-2 gap-4">{apiPermissions}</div>
								</>
							)
						)}
					</div>
				</AccordionWrapperVanilla>
			);
		});
	const handleGoBack = () => {
		navigate("/users", { state: { filterData: filterData } });
	};
	return (
		<>
			<div className="cust-row flex-algn-cent">
				<div className="cust-col-5">
					<h1 className="title">{isUpdate ? "Update User" : "Add User"}</h1>
				</div>
				<div className="cust-col-5 flex-jc-end">
					<div>
						<Button text="Go Back" style={buttonClasses.lynkitOrangeEmpty} onClick={handleGoBack} />
					</div>
				</div>
			</div>
			<hr className="bgg" />
			<form onSubmit={handleSubmit(onSubmit)}>
				<div className="form-inputs" style={{ marginBottom: "2rem", marginTop: "1.5rem" }}>
					<Controller
						name="name"
						control={control}
						rules={{ required: "Name is required" }}
						render={({ field }) => (
							<Input label="Name" placeholder="Enter Name" name="name" id="name" value={field.value} onChange={field.onChange} error={errors.name?.message} required />
						)}
					/>

					<Controller
						name="email"
						control={control}
						rules={{
							required: "Email is required",
							validate: (value) => valid_email(value) || "Enter a valid email",
						}}
						render={({ field }) => (
							<Input
								readOnly={!!updateData && isUpdate}
								type="email"
								label="Email"
								placeholder="Enter Email"
								name="email"
								id="email"
								value={field.value}
								onChange={field.onChange}
								error={errors.email?.message}
								required
							/>
						)}
					/>

					<Controller
						name="mobile"
						control={control}
						rules={{
							required: "Mobile is required",
							validate: (value) => valid_mobile(value) || "Enter a valid mobile number",
						}}
						render={({ field }) => (
							<Input
								type="tel"
								label="Mobile"
								placeholder="Enter Mobile"
								name="mobile"
								id="mobile"
								value={field.value}
								onChange={(e) => {
									if (!isNaN(e.target.value)) {
										field.onChange(e);
									}
								}}
								error={errors.mobile?.message}
								required
							/>
						)}
					/>

					{!isUpdate && (
						<Controller
							name="password"
							control={control}
							rules={{
								required: "Password is required",
								validate: (value) =>
									valid_password(value) ||
									"Password should contain minimum 8 characters, including at least 1 uppercase letter, 1 lowercase letter, 1 special character, and 1 number.",
							}}
							render={({ field }) => (
								<Input
									type={passwordType}
									label="Password"
									placeholder="Enter Password"
									onChange={field.onChange}
									name="password"
									id="password"
									value={field.value}
									adornment={{
										is: true,
										onClick: changeType,
										type: passwordType === "password" ? "eye" : "eyeClose",
									}}
									required
									error={errors.password?.message}
								/>
							)}
						/>
					)}

					{!isUpdate && (
						<Controller
							name="confirmPassword"
							control={control}
							rules={{
								required: "Please enter your password again",
								validate: (value) => value === password || "Passwords do not match",
							}}
							render={({ field }) => (
								<Input
									label="Confirm Password"
									placeholder="Confirm Password"
									onChange={field.onChange}
									name="confirmPassword"
									id="confirmPassword"
									value={field.value}
									type={confirmPasswordType}
									adornment={{
										is: true,
										onClick: changeConfirmType,
										type: confirmPasswordType === "password" ? "eye" : "eyeClose",
									}}
									required
									error={errors.confirmPassword?.message}
								/>
							)}
						/>
					)}

					{userDetail?.userType == "superAdmin" && (
						<Controller
							name="orgId"
							control={control}
							rules={{ required: "Organization name is required" }}
							render={({ field }) => (
								<Dropdown
									id="orgIdAdd"
									name="orgId"
									label="Organization"
									onChange={(option) => {
										onDropdownChange(option, "orgId");
										field.onChange(option?.value || "");
									}}
									options={orgOptions}
									value={orgOptions.find((_) => _.value == field.value) || null}
									required
									error={errors.orgId?.message}
								/>
							)}
						/>
					)}

					<Controller
						name="userRole"
						control={control}
						rules={{ required: "User role is required" }}
						render={({ field }) => (
							<Dropdown
								id="userRole"
								name="userRole"
								label="User Role"
								onChange={(option) => {
									onDropdownChange(option, "userRole");
									field.onChange(option?.value || "");
								}}
								options={roleOptions}
								value={roleOptions.find((_) => _.value == field.value) || null}
								required
								error={errors.userRole?.message}
							/>
						)}
					/>
				</div>
				{/* Non-Plant Permissions */}
				{nonPlantPermissions.length > 0 && (
					<div className="non-plant-permissions-section">
						<h3 className="title">General Permissions</h3>
						<div className="perm_wrap grid grid-cols-2 gap-4">{nonPlantPermissions}</div>
					</div>
				)}
				{/* Plants Selection */}
				{isPlantLoading ? (
					<Loader size="1rem" />
				) : (
					plantOptions.length > 0 && (
						<div className="plants-selection-section">
							<h3 className="title" style={{ marginTop: "2rem" }}>
								Select Warehouse
							</h3>
							{errors.plants && <p className="error-message">{errors.plants}</p>}
							<div
								className="plants-checkbox-container "
								style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "14px 24px", maxWidth: "100%", margin: "14px 0px" }}
							>
								{plantCheckboxes}
							</div>
						</div>
					)
				)}

				{/* Plant Accordions with VMS permissions */}
				{plantAccordions}

				<div className="d-flex justify-content-center" style={{ height: "79px" }}>
					<Button type="submit" text={isUpdate ? "Update" : "Submit"} style={{ ...buttonClasses.lynkitOrangeFill, width: "15%" }} disabled={isAdding || isUpdating} />
				</div>
			</form>
		</>
	);
}

const CheckboxController = memo(({ name, control, label, setValue, childrenKeys, onChange }) => {
	return (
		<Controller
			name={`permissions.${name}`}
			control={control}
			render={({ field }) => {
				return (
					<Checkbox
						label={label}
						checked={!!field.value}
						onChange={(e) => {
							field.onChange(e.target.checked);
							// Call the onChange callback if provided
							if (onChange) onChange(e.target.checked);

							// If this is a parent checkbox with children, update all children
							if (childrenKeys?.length) {
								childrenKeys.forEach((childKey) => {
									setValue(`permissions.${childKey}`, e.target.checked, { shouldDirty: true });
								});
							}
						}}
					/>
				);
			}}
		/>
	);
});

export default AddUserForm;

const capitalizeWords = (str) => {
	return str
		.split(" ")
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(" ")
		.split(/(?=[A-Z])/)
		.join(" ");
};
